/**
 * Global Authentication-Aware API Service
 * Handles auth errors and token validation across all providers
 */

import { logger } from '@/services/LoggingService';
import { authService } from '@/services/AuthService';

/**
 * Enhanced error handler for auth-aware API calls
 * Provider-agnostic auth error handling with customizable cache clearing
 * @param error - The error object to check
 * @param operation - Operation name for logging
 * @param clearCacheCallback - Provider-specific cache clearing function
 * @param shouldRetry - Whether to attempt retry after token refresh
 */
export async function handleAuthError(
  error: any,
  operation: string,
  clearCacheCallback: (reason: string) => void,
  shouldRetry: boolean = true
): Promise<{ shouldRetry: boolean; refreshed: boolean }> {
  const isAuthError = error?.status === 401 || error?.status === 406 || 
                     error?.message?.toLowerCase().includes('unauthorized') ||
                     error?.message?.toLowerCase().includes('not acceptable');

  if (!isAuthError) {
    return { shouldRetry: false, refreshed: false };
  }

  logger.warn('AuthAwareAPI', 'Auth error detected', {
    operation,
    status: error?.status,
    message: error?.message,
    shouldRetry
  });

  // Clear cache immediately using provider's callback
  clearCacheCallback(`${error?.status || 'AUTH_ERROR'} during ${operation}`);

  if (!shouldRetry) {
    return { shouldRetry: false, refreshed: false };
  }

  // Attempt token refresh
  logger.info('AuthAwareAPI', 'Attempting token refresh', { operation });
  const refreshed = await authService.refreshSession();

  if (refreshed) {
    logger.info('AuthAwareAPI', 'Token refresh successful, retry available', { operation });
    return { shouldRetry: true, refreshed: true };
  } else {
    logger.error('AuthAwareAPI', 'Token refresh failed', { operation });
    return { shouldRetry: false, refreshed: false };
  }
}

/**
 * Proactive token check before making API calls
 * @param operation - The operation being performed for logging
 * @returns Promise resolving to boolean indicating if token is valid/refreshed
 */
export async function ensureValidToken(operation: string): Promise<boolean> {
  try {
    const isNearExpiry = await authService.isTokenNearExpiry();
    
    if (isNearExpiry) {
      logger.info('AuthAwareAPI', 'Token near expiry, refreshing preemptively', { operation });
      const refreshed = await authService.refreshSession();
      
      if (!refreshed) {
        logger.error('AuthAwareAPI', 'Preemptive token refresh failed', { operation });
        return false;
      }
      
      logger.info('AuthAwareAPI', 'Preemptive token refresh successful', { operation });
    }
    
    return true;
  } catch (err) {
    logger.error('AuthAwareAPI', 'Error in token validation', {
      operation,
      error: err instanceof Error ? err.message : String(err)
    });
    return false;
  }
}